import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const links = [
  { to: "/services", label: "Services" },
  { to: "/approach", label: "Approach" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 bg-[#F8FBFF]/95 backdrop-blur-sm transition-shadow duration-300 ${
          scrolled ? "shadow-[0_2px_16px_rgba(10,31,68,0.08)]" : ""
        }`}
      >
        <div className="mx-auto flex h-[76px] max-w-[1240px] items-center justify-between px-5 sm:px-8">
          <Link to="/" aria-label="RevGen Genius home" data-testid="header-logo">
            <img
              src="/assets/revgengenius-lockup-blue.png"
              alt="RevGen Genius"
              className="h-8 w-auto sm:h-9"
            />
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="nav-link">
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden lg:block">
              <Link to="/contact" data-testid="header-book-assessment-button" className="btn-primary">
                Book a Free Assessment <ArrowRight size={17} />
              </Link>
            </div>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-[#1F5FFF] text-[#1F5FFF] lg:hidden"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-drawer"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#F8FBFF] px-6 pb-8 pt-5 lg:hidden"
          >
            <div className="flex items-center justify-between">
              <img src="/assets/revgengenius-lockup-blue.png" alt="RevGen Genius" className="h-8 w-auto" />
              <button
                type="button"
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-[#1F5FFF] text-[#1F5FFF]"
              >
                <X size={22} />
              </button>
            </div>
            <nav aria-label="Mobile" className="mt-12 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="font-heading block rounded-2xl px-4 py-4 text-2xl font-semibold text-[#0A1F44] transition-colors hover:bg-[#EAF1FF] hover:text-[#1F5FFF]"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto pt-8">
              <Link to="/contact" data-testid="mobile-drawer-book-button" className="btn-primary w-full text-base">
                Book a Free Assessment <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
